import { useEffect, useState } from 'react';
import type { FighterId } from '@archon3d/core';
import type { BattleView, FighterStatus } from '../game/battleFeed';
import { TIME_UP, suddenDeathWarning } from '../game/duelClock';
import { coarsePointer } from '../input/touchFighterInput';

interface Props {
  battle: BattleView;
  /** A person gives up: the first person's side unless one is named. */
  onForfeit: (side?: FighterId) => void;
  /** The outcome has been read: back to the board now. */
  onContinue?: () => void;
}

/** Hit points as a player reads them: whole numbers, never the fitted decimals underneath. */
const health = (f: { hp: number; maxHp: number }) => `${Math.round(f.hp)}/${Math.round(f.maxHp)}`;
/**
 * A cursed fighter fights slowed (D-31), and a burning one is losing health to the fire: its name
 * says so, since nothing else in the panel would.
 */
const label = (f: FighterStatus) =>
  [f.name, ...(f.cursed ? ['cursed'] : []), ...(f.burning ? ['burning'] : [])].join(' · ');
/** A fighter's pill in the panel, which throbs while it burns. */
const pill = (f: FighterStatus) => (f.burning ? 'pill burning' : 'pill');

/**
 * Whether the player folded the panel away, kept from one duel to the next: a player who wants the
 * arena to themselves wants it every fight, not once.
 */
let folded = false;

/**
 * The battle panel over the arena: one row of names, health, the clock and Forfeit, and a line
 * for what matters now (how to fight, sudden death, the outcome). One row on every device, because
 * the panel is drawn over the arena and the arena has to stay whole: the camera keeps its frame
 * out from under the panel (`scenes/arena/arenaFrame.ts`), so every line the panel does not need
 * is arena the player sees.
 */
export function BattleOverlay({ battle, onForfeit, onContinue }: Props) {
  const mine = battle.human === 'defender';
  const me = mine ? battle.defender : battle.attacker;
  const enemy = mine ? battle.attacker : battle.defender;
  const seconds = Math.floor(battle.time);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  const hint = controlsHint(battle);
  const warning = suddenDeathWarning(battle.time, battle.suddenDeathAt, battle.suddenDeath);
  const [hidden, setHidden] = useState(folded);
  const fold = (away: boolean) => {
    folded = away;
    setHidden(away);
  };
  // The outcome is always shown: a folded panel is about the fight, not its result.
  const shown = !hidden || battle.phase === 'over';
  const handle = (
    <button
      className="hud-handle"
      data-testid="battle-hud-handle"
      aria-expanded={shown}
      aria-controls="battle-hud"
      title={shown ? 'Hide the battle panel' : 'Show the battle panel'}
      onClick={() => fold(shown)}
    >
      {shown ? 'Hide' : 'Show'}
    </button>
  );

  const versus = <Versus battle={battle} />;

  // Holding the button that gives up: told to keep holding, with how far to go, folded or not.
  const holding = battle.forfeitHold ? <ForfeitHold hold={battle.forfeitHold} /> : null;

  if (!shown)
    return (
      <div className="overlay battle" data-testid="battle-overlay">
        <div className="column">
          <section className="panel compact battle-hud folded" aria-label="Battle">
            <div className="row hud-row">{handle}</div>
            {holding}
          </section>
        </div>
        {versus}
      </div>
    );

  return (
    <div className="overlay battle" data-testid="battle-overlay">
      <div className="column">
        <section className="panel compact battle-hud" id="battle-hud" aria-label="Battle">
          <div className="row hud-row">
            <span className={pill(me)}>
              {label(me)} {health(me)}
            </span>
            <span className={pill(enemy)}>
              {label(enemy)} {health(enemy)}
            </span>
            <span className="pill">{clock}</span>
            {battle.phase === 'fighting' &&
              battle.human &&
              (battle.humans.length > 1 ? (
                // Two people at the table: each gives up their own fighter, by name.
                battle.humans.map((side) => (
                  <ForfeitButton
                    key={side}
                    label={`${(side === 'attacker' ? battle.attacker : battle.defender).name} forfeits`}
                    onForfeit={() => onForfeit(side)}
                  />
                ))
              ) : (
                <ForfeitButton label="Forfeit" onForfeit={() => onForfeit()} />
              ))}
            {battle.phase !== 'over' && handle}
          </div>
          {battle.phase === 'over' ? (
            // One line still, so the panel keeps its height and the arena its frame.
            <p className="outcome">
              <span data-testid="battle-outcome">{outcomeText(battle)}</span>{' '}
              <span className="outcome-summary" data-testid="battle-summary">
                {fightSummary(battle)}
              </span>
              {onContinue && (
                <button onClick={onContinue} data-testid="battle-continue">
                  Continue
                </button>
              )}
            </p>
          ) : holding ? (
            holding
          ) : battle.missingController ? (
            <p className="hint warn" role="alert" data-testid="controller-missing">
              {battle.missingController}’s controller is not connected · press a button on it
            </p>
          ) : battle.suddenDeath ? (
            <p className="hint warn">Sudden death: the arena burns from the edges in.</p>
          ) : warning ? (
            <p className="hint warn" data-testid="sudden-death-warning">
              {warning}
            </p>
          ) : hint ? (
            <p className="hint">{hint}</p>
          ) : null}
        </section>
      </div>
      {versus}
    </div>
  );
}

/** Keep holding: the button, and a bar that fills as the hold goes on. */
function ForfeitHold({ hold }: { hold: { share: number; button: string } }) {
  const percent = Math.round(Math.min(1, Math.max(0, hold.share)) * 100);
  return (
    <p className="hint warn forfeit-hold" role="status" data-testid="forfeit-hold">
      Keep holding {hold.button} to forfeit
      <span
        className="hold-bar"
        role="progressbar"
        aria-label="Forfeit"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span style={{ width: `${percent}%` }} />
      </span>
    </p>
  );
}

/** How long a first press on Forfeit waits for the second. */
const FORFEIT_CONFIRM_MS = 3000;

/**
 * Giving up loses the square and the piece, and a click meant for the arena can land on the
 * panel: the first press asks, the second within three seconds gives up (client-arena:quality#11).
 * A controller's button has to be held for the same reason.
 */
function ForfeitButton({ label, onForfeit }: { label: string; onForfeit: () => void }) {
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!asking) return;
    const timer = setTimeout(() => setAsking(false), FORFEIT_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [asking]);
  return (
    <button
      className={asking ? 'danger' : undefined}
      data-testid="forfeit"
      onClick={() => (asking ? onForfeit() : setAsking(true))}
    >
      {asking ? `${label}? Press again` : label}
    </button>
  );
}

/**
 * Before the fight: the two fighters, what the square's light gives each, and a count of three,
 * then "Fight!" as it begins (x-aaa-gap#3). Drawn over the arena, not beside it, and gone once the
 * fight is on.
 */
function Versus({ battle }: { battle: BattleView }) {
  if (battle.countdown === null) return null;
  const { attacker, defender } = battle;
  return (
    <div className="battle-versus" data-testid="battle-versus" aria-live="polite">
      {battle.countdown > 0 && (
        <>
          <p className="versus-names">
            <span>{attacker.name}</span> <em>against</em> <span>{defender.name}</span>
          </p>
          <p className="versus-light">{lightLine(attacker, defender)}</p>
        </>
      )}
      {/* A new element per number, so each one pops in. */}
      <p key={battle.countdown} className="versus-count" data-testid="battle-count">
        {battle.countdown === 0 ? 'Fight!' : battle.countdown}
      </p>
    </div>
  );
}

/** What the square's light gives each fighter, the part of their hit points the board decided. */
function lightLine(a: BattleView['attacker'], b: BattleView['defender']): string {
  const give = (f: BattleView['attacker']) => `${f.name} +${f.light ?? 0}`;
  return `The square's light: ${give(a)}, ${give(b)} hit points`;
}

/** How the fight went, under its verdict: how long it lasted, what the winner has left. */
export function fightSummary(battle: BattleView): string {
  const seconds = `${Math.max(1, Math.round(battle.time))} s`;
  if (!battle.winner) return `${seconds} of fighting.`;
  const winner = battle.winner === 'attacker' ? battle.attacker : battle.defender;
  return `${seconds} of fighting · ${winner.name} keeps ${health(winner)} hit points.`;
}

function outcomeText(battle: BattleView): string {
  // The clock ended it: the verdict comes with its reason.
  return battle.timedOut ? `${TIME_UP}. ${verdict(battle)}` : verdict(battle);
}

function verdict(battle: BattleView): string {
  // Watching, or two people fighting each other: the fighter's name says who won.
  if (!battle.human || battle.outcome === null) {
    const winner = battle.winner === 'attacker' ? battle.attacker : battle.defender;
    return battle.winner ? `${winner.name} wins.` : 'Both fall.';
  }
  switch (battle.outcome) {
    case 'won':
      return battle.practice ? 'Victory!' : 'Victory! The square is yours.';
    case 'lost':
      return battle.practice ? 'Defeated.' : 'Defeated. The square is lost.';
    default:
      return 'Both fall.';
  }
}

function controlsHint(battle: BattleView): string | null {
  // The count before the fight is when there is time to read how to fight.
  if (!battle.human) return 'Watching the duel.';
  if (battle.humans.length > 1)
    return 'Each on their own controller · pad: left stick moves, right stick aims, hold Start to forfeit';
  // One player with a pad in hand learns its buttons, how to give up among them: a tap on Start
  // did nothing, and a hold ended the duel unwarned (client-input:quality#2).
  if (battle.pad)
    return 'Pad: left stick moves · right stick aims · RT attacks · LT dashes · hold Start to forfeit';
  // The same test the arena uses to decide which controls to give the player. A phone was being
  // told about WASD and a cursor it has neither of; its stick and buttons are under the thumbs
  // and explain themselves, and the line they would take is arena instead.
  if (coarsePointer()) return null;
  return 'WASD or arrows move and turn · Space dashes · left mouse or F attacks toward the cursor';
}
